import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemedCard } from '../components/ThemedCard';
import { ThemedButton } from '../components/ThemedButton';
import { useTheme } from '../lib/theme-provider';
import { Crown, Home, RotateCcw, Share2, Users } from 'lucide-react';
import MVPVotingModal from '../components/MVPVotingModal';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  role: string;
  team: 'good' | 'evil';
  isMVP: boolean;
  voteCount: number;
  isCurrentPlayer: boolean;
}

interface Mission {
  round: number;
  players: number;
  result: 'success' | 'fail';
  votes: { approve: number; reject: number };
}

export default function GameEnd() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showMVPModal, setShowMVPModal] = useState(false);

  // 模拟数据
  const winner: 'good' | 'evil' = 'good';
  const mvp: Player = {
    id: '1',
    seatNumber: 1,
    nickname: 'Drunk',
    role: '梅林',
    team: 'good',
    isMVP: true,
    voteCount: 5,
    isCurrentPlayer: false,
  };

  const players: Player[] = [
    { id: '1', seatNumber: 1, nickname: 'Drunk', role: '梅林', team: 'good', isMVP: true, voteCount: 5, isCurrentPlayer: false },
    { id: '2', seatNumber: 2, nickname: '帅哥', role: '派西维尔', team: 'good', isMVP: false, voteCount: 2, isCurrentPlayer: false },
    { id: '3', seatNumber: 3, nickname: '才才', role: '忠臣', team: 'good', isMVP: false, voteCount: 1, isCurrentPlayer: false },
    { id: '4', seatNumber: 4, nickname: '昕宇', role: '忠臣', team: 'good', isMVP: false, voteCount: 0, isCurrentPlayer: false },
    { id: '5', seatNumber: 5, nickname: '周鼠鼠', role: '忠臣', team: 'good', isMVP: false, voteCount: 0, isCurrentPlayer: false },
    { id: '6', seatNumber: 6, nickname: 'boyixi', role: '刺客', team: 'evil', isMVP: false, voteCount: 1, isCurrentPlayer: false },
    { id: '7', seatNumber: 7, nickname: 'Hy', role: '莫甘娜', team: 'evil', isMVP: false, voteCount: 0, isCurrentPlayer: false },
    { id: '8', seatNumber: 8, nickname: '丁丁', role: '莫德雷德', team: 'evil', isMVP: false, voteCount: 0, isCurrentPlayer: false },
    { id: '9', seatNumber: 9, nickname: '好人火火', role: '奥伯伦', team: 'evil', isMVP: false, voteCount: 0, isCurrentPlayer: false },
    { id: '10', seatNumber: 10, nickname: '还是要用...', role: '忠臣', team: 'good', isMVP: false, voteCount: 0, isCurrentPlayer: true },
  ];

  const missions: Mission[] = [
    { round: 1, players: 3, result: 'success', votes: { approve: 6, reject: 4 } },
    { round: 2, players: 4, result: 'success', votes: { approve: 7, reject: 3 } },
    { round: 3, players: 4, result: 'success', votes: { approve: 8, reject: 2 } },
    { round: 4, players: 5, result: 'fail', votes: { approve: 5, reject: 5 } },
    { round: 5, players: 5, result: 'success', votes: { approve: 9, reject: 1 } },
  ];

  const handleVoteMVP = (targetSeat: number | null) => {
    console.log('MVP投票:', targetSeat);
    setShowMVPModal(false);
  };

  const handleShare = () => {
    // TODO: 实现分享功能
    console.log('分享战绩');
  };

  return (
    <div
      className="min-h-screen p-4"
      style={{ backgroundColor: theme.colors['bg-primary'] }}
    >
      {/* 标题 */}
      <div className="text-center py-6">
        <h1
          className="text-3xl font-bold mb-2"
          style={{
            color: winner === 'good' ? '#4ade80' : '#f87171',
          }}
        >
          {winner === 'good' ? '正义阵营获胜！' : '邪恶阵营获胜！'}
        </h1>
        <p
          className="text-sm"
          style={{ color: theme.colors['text-secondary'] }}
        >
          房间号: OLTP05 · 10人局
        </p>
      </div>

      {/* MVP展示 */}
      {mvp && (
        <ThemedCard
          className="mb-4 p-6"
          style={{
            backgroundColor: `${theme.colors['accent-gold']}10`,
            border: `2px solid ${theme.colors['accent-gold']}`,
          }}
        >
          <div className="flex flex-col items-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-3"
              style={{
                backgroundColor: theme.colors['accent-gold'],
                border: `4px solid ${theme.colors['accent-gold']}`,
                boxShadow: `0 0 20px ${theme.colors['accent-gold']}50`,
              }}
            >
              <Crown size={40} style={{ color: theme.colors['bg-primary'] }} />
            </div>
            <h2
              className="text-xl font-bold mb-1"
              style={{ color: theme.colors['accent-gold'] }}
            >
              MVP
            </h2>
            <p
              className="text-lg font-medium mb-1"
              style={{ color: theme.colors['text-primary'] }}
            >
              {mvp.nickname}
            </p>
            <p
              className="text-sm mb-2"
              style={{ color: theme.colors['text-secondary'] }}
            >
              #{mvp.seatNumber} · {mvp.role}
            </p>
            <div
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{
                backgroundColor: `${theme.colors['accent-gold']}20`,
                color: theme.colors['accent-gold'],
              }}
            >
              {mvp.voteCount} 票
            </div>
          </div>
        </ThemedCard>
      )}

      {/* 任务结果 */}
      <ThemedCard className="mb-4 p-4">
        <h3
          className="text-lg font-bold mb-3 text-center"
          style={{ color: theme.colors['text-primary'] }}
        >
          任务结果
        </h3>
        <div className="flex items-center justify-center gap-2">
          {missions.map((mission) => (
            <div key={mission.round} className="flex flex-col items-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-1"
                style={{
                  backgroundColor:
                    mission.result === 'success' ? '#4ade80' : '#f87171',
                  color: theme.colors['bg-primary'],
                }}
              >
                {mission.result === 'success' ? '✓' : '✗'}
              </div>
              <span
                className="text-xs"
                style={{ color: theme.colors['text-muted'] }}
              >
                {mission.players}人
              </span>
            </div>
          ))}
        </div>
      </ThemedCard>

      {/* 身份揭示 */}
      <ThemedCard className="mb-4 p-4">
        <h3
          className="text-lg font-bold mb-3 text-center"
          style={{ color: theme.colors['text-primary'] }}
        >
          身份揭示
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {players.map((player) => (
            <div
              key={player.id}
              className="flex items-center gap-2 p-2 rounded-lg"
              style={{
                backgroundColor: theme.colors['bg-primary'],
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                style={{
                  backgroundColor:
                    player.team === 'good'
                      ? '#4ade80'
                      : '#f87171',
                  color: theme.colors['bg-primary'],
                }}
              >
                {player.nickname[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium truncate"
                  style={{ color: theme.colors['text-primary'] }}
                >
                  {player.nickname}
                </p>
                <p
                  className="text-xs"
                  style={{ color: theme.colors['text-muted'] }}
                >
                  {player.role}
                </p>
              </div>
              {player.isMVP && (
                <Crown size={14} style={{ color: theme.colors['accent-gold'] }} />
              )}
            </div>
          ))}
        </div>
      </ThemedCard>

      {/* 操作按钮 */}
      <div className="flex gap-3 mb-4">
        <ThemedButton
          variant="secondary"
          onClick={() => navigate('/online-lobby')}
          className="flex-1"
        >
          <Home size={18} className="mr-1" />
          返回大厅
        </ThemedButton>
        <ThemedButton onClick={handleShare} className="flex-1">
          <Share2 size={18} className="mr-1" />
          分享战绩
        </ThemedButton>
      </div>

      <ThemedButton
        onClick={() => setShowMVPModal(true)}
        className="w-full"
        style={{
          backgroundColor: theme.colors['accent-gold'],
        }}
      >
        <Users size={18} className="mr-1" />
        投票MVP
      </ThemedButton>

      {/* MVP投票弹窗 */}
      <MVPVotingModal
        isOpen={showMVPModal}
        players={players}
        onClose={() => setShowMVPModal(false)}
        onVote={handleVoteMVP}
      />
    </div>
  );
}
